import { Box, Flex, LinkBox, Tag, Text } from '@chakra-ui/react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useMemo } from 'react';

import { getReadingTime } from '#features/blog/utils/getReadingTime';
import { useFormatDistanceToNow } from '#features/date';
import { Image, Link, Panel } from '#features/ui';

type BlogHeroPostProps = {
  slug: string;
  previewImage: string;
  title: string;
  tags?: string;
  date?: string;
  ogDescription?: string;
  content?: string;
  readingTime?: number;
};

export const BlogHeroPost = ({
  title,
  previewImage,
  slug,
  tags,
  date,
  ogDescription,
  content,
  readingTime: suppliedReadingTime,
}: BlogHeroPostProps) => {
  const { _ } = useLingui();
  const formatDistanceToNow = useFormatDistanceToNow();
  const readingTime = useMemo(
    () => suppliedReadingTime ?? (content ? getReadingTime(content) : null),
    [content, suppliedReadingTime]
  );

  return (
    <Panel asChild variant="object" interactive>
      <LinkBox
        as="article"
        display="flex"
        w="100%"
        overflow="hidden"
        flexDir={{ base: 'column', md: 'row' }}
        transition="background-color var(--chakra-durations-moderate) var(--chakra-easings-ease-out)"
        css={{
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
        mb={8}
      >
        {/* Image */}
        <Box
          w={{ base: '100%', md: '60%' }}
          aspectRatio={{ base: '16 / 9', md: 'auto' }}
          minH={{ md: '320px' }}
          overflow="hidden"
          flexShrink={0}
        >
          <Image
            src={previewImage}
            alt={title}
            style={{
              objectFit: 'cover',
              objectPosition: 'top center',
              width: '100%',
              height: '100%',
            }}
          />
        </Box>

        {/* Content */}
        <Flex direction="column" p={{ base: 5, md: 8 }} gap={4} justify="center" flex={1}>
          {/* Tags + reading time */}
          <Flex gap={2} alignItems="center" flexWrap="wrap">
            {tags &&
              tags
                .split(',')
                .map((tag) => tag.trim())
                .filter(Boolean)
                .slice(0, 3)
                .map((tag) => (
                  <Tag.Root key={tag} size="md">
                    <Tag.Label textStyle="label">{tag}</Tag.Label>
                  </Tag.Root>
                ))}
            {readingTime && (
              <Text textStyle="caption" color="fg.muted">
                {readingTime} {_(msg({ id: 'blog.readingTime', message: 'min read' }))}
              </Text>
            )}
          </Flex>

          {/* Title */}
          <Text as="h2" textStyle="blog.featuredTitle" lineClamp={3}>
            {title}
          </Text>

          {/* Description */}
          {ogDescription && (
            <Text textStyle="blog.featuredDescription" color="fg.muted" lineClamp={3}>
              {ogDescription}
            </Text>
          )}

          {/* Date */}
          {date && (
            <Text textStyle="body" color="fg.muted">
              {formatDistanceToNow(date)}
            </Text>
          )}
        </Flex>

        <Link to="/{-$locale}/blog/$slug" params={{ slug }} overlay aria-label={title} />
      </LinkBox>
    </Panel>
  );
};
